import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus,
  Calendar,
  Camera,
  QrCode,
  Sparkles,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";

export default function DashboardPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "",
    date: "",
    description: "",
    photo_limit: 0,
    plan: "basic",
  });
  const [enableLimit, setEnableLimit] = useState(false);
  const [plansInfo, setPlansInfo] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => toast.error("Erro ao carregar eventos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/pix-info").then((r) => setPlansInfo(r.data.plans)).catch(() => {});
  }, []);

  const createEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        date: form.date,
        description: form.description,
        plan: form.plan,
        photo_limit: enableLimit ? Math.max(1, Number(form.photo_limit || 0)) : 0,
      };
      const { data } = await api.post("/events", payload);
      toast.success("Evento criado!");
      setShowCreate(false);
      setForm({ name: "", date: "", description: "", photo_limit: 0, plan: "basic" });
      setEnableLimit(false);
      nav(`/dashboard/events/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const statusPill = (ev) => {
    if (ev.payment_status === "pending")
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
          <Clock className="w-3 h-3" /> Aguardando pagamento
        </span>
      );
    if (ev.status === "ended")
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/10 text-white/60 border border-white/20">
          <XCircle className="w-3 h-3" /> Encerrado
        </span>
      );
    if (!ev.frame_path)
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">
          <Sparkles className="w-3 h-3" /> Enviar moldura
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30">
        <CheckCircle2 className="w-3 h-3" /> Ativo
      </span>
    );
  };

  return (
    <div className="min-h-screen text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
              Painel do organizador
            </span>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
              Olá, {user?.name?.split(" ")[0]}
              {user?.role === "vip" && (
                <span className="badge-vip ml-3 align-middle">
                  <Sparkles className="w-3 h-3" /> VIP
                </span>
              )}
            </h1>
            <p className="text-white/60 mt-1">Gerencie seus eventos e galerias.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-neon"
            data-testid="new-event-btn"
          >
            <Plus className="w-4 h-4" /> Novo evento
          </button>
        </div>

        {showCreate && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-6 mb-10"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl tracking-tight">
                Criar evento
              </h2>
              <button
                onClick={() => setShowCreate(false)}
                className="text-white/50 hover:text-white transition-colors"
                data-testid="close-create-form"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={createEvent}
              className="grid md:grid-cols-2 gap-4"
              data-testid="create-event-form"
            >
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 block mb-2">
                  Escolha o plano
                </label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    {
                      key: "basic",
                      title: "Básico",
                      price: plansInfo?.basic?.price || 49.9,
                      bullets: ["1 moldura personalizada", `${plansInfo?.basic?.total_photos || 300} fotos no total`, "QR + galeria + ZIP"],
                      color: "#00F0FF",
                    },
                    {
                      key: "premium",
                      title: "Premium",
                      price: plansInfo?.premium?.price || 99.9,
                      bullets: ["3 molduras (convidado escolhe)", "Fotos ilimitadas", "Tudo do Básico incluso"],
                      color: "#FF007F",
                    },
                  ].map((p) => {
                    const active = form.plan === p.key;
                    const isVip = user?.role === "vip";
                    return (
                      <button
                        type="button"
                        key={p.key}
                        onClick={() => setForm({ ...form, plan: p.key })}
                        data-testid={`plan-${p.key}`}
                        className={`text-left rounded-2xl p-4 border transition-all ${
                          active
                            ? "border-transparent"
                            : "border-white/10 hover:border-white/30"
                        }`}
                        style={
                          active
                            ? {
                                background: `${p.color}15`,
                                borderColor: `${p.color}80`,
                                boxShadow: `0 0 20px ${p.color}40`,
                              }
                            : { background: "rgba(255,255,255,0.03)" }
                        }
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-display font-bold tracking-tight" style={{ color: active ? p.color : "#fff" }}>
                            {p.title}
                          </span>
                          {isVip ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF007F]/20 text-[#FF007F] font-bold">
                              GRÁTIS VIP
                            </span>
                          ) : (
                            <span className="text-sm font-bold" style={{ color: p.color }}>
                              R$ {p.price.toFixed(2).replace(".", ",")}
                            </span>
                          )}
                        </div>
                        <ul className="text-xs text-white/70 space-y-1">
                          {p.bullets.map((b, i) => (
                            <li key={i} className="flex gap-1.5">
                              <span style={{ color: p.color }}>✓</span> {b}
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">
                  Nome do evento
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-dark mt-2"
                  placeholder="Ex: Casamento Ana & Léo"
                  data-testid="event-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">
                  Data
                </label>
                <input
                  required
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="input-dark mt-2"
                  data-testid="event-date"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">
                  Descrição (opcional)
                </label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-dark mt-2 resize-none"
                  placeholder="Uma descrição curta"
                  data-testid="event-description"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableLimit}
                    onChange={(e) => setEnableLimit(e.target.checked)}
                    className="w-4 h-4 accent-[#FF007F]"
                    data-testid="toggle-photo-limit"
                  />
                  Definir limite de fotos por convidado?
                </label>
                {enableLimit ? (
                  <input
                    type="number"
                    min="1"
                    max="200"
                    value={form.photo_limit || ""}
                    onChange={(e) =>
                      setForm({ ...form, photo_limit: Number(e.target.value) })
                    }
                    className="input-dark mt-2"
                    placeholder="Ex: 10 fotos por convidado"
                    data-testid="event-photo-limit"
                  />
                ) : (
                  <p className="mt-2 text-xs text-white/40">
                    Sem limite — cada convidado pode enviar quantas fotos quiser.
                  </p>
                )}
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-neon w-full"
                  data-testid="submit-create-event"
                >
                  {creating ? "Criando..." : "Criar evento"}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {loading ? (
          <div className="text-white/50 text-center py-16">Carregando...</div>
        ) : events.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="empty-state">
            <Camera className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl tracking-tight">
              Nenhum evento ainda
            </h3>
            <p className="text-white/50 mt-2 mb-6">
              Comece criando seu primeiro evento agora mesmo.
            </p>
            <button onClick={() => setShowCreate(true)} className="btn-neon">
              <Plus className="w-4 h-4" /> Criar primeiro evento
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {events.map((ev) => (
              <Link
                key={ev.id}
                to={`/dashboard/events/${ev.id}`}
                data-testid={`event-card-${ev.id}`}
                className="glass rounded-2xl p-6 hover-lift block group"
              >
                <div className="flex items-start justify-between mb-4">
                  {statusPill(ev)}
                  {ev.is_vip && (
                    <span className="badge-vip">
                      <Sparkles className="w-3 h-3" /> VIP
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight group-hover:text-[#FF007F] transition-colors">
                  {ev.name}
                </h3>
                <div className="flex items-center gap-2 text-xs text-white/50 mt-2">
                  <Calendar className="w-3 h-3" />
                  {new Date(ev.date).toLocaleDateString("pt-BR")}
                </div>
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2 text-sm">
                    <Camera className="w-4 h-4 text-[#00F0FF]" />
                    <span className="font-semibold">{ev.photos_count}</span>
                    <span className="text-white/40">fotos</span>
                  </div>
                  <QrCode className="w-4 h-4 text-white/30 group-hover:text-[#FF007F] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
